import { RefObject, useCallback, useLayoutEffect } from "react";

export const MARQUEE_SPEED_PX_PER_SEC = 60;
export const MIN_MARQUEE_DURATION_SEC = 5;
export type MarqueeMode = "pass" | "loop";

type ResizeObserverLike = {
  observe: (target: Element) => void;
  disconnect: () => void;
};

function applyMarqueeStyles(container: HTMLElement, content: HTMLElement, mode: MarqueeMode) {
  const containerWidth = container.offsetWidth;
  const contentWidth = content.scrollWidth;
  if (contentWidth <= 0) return;

  let travelDistance: number;
  if (mode === "loop") {
    travelDistance = contentWidth / 2;
    container.style.setProperty("--marquee-start", "0px");
    container.style.setProperty("--marquee-end", `-${travelDistance}px`);
  } else {
    if (containerWidth <= 0) return;
    const startPos = containerWidth;
    const endPos = -contentWidth;
    travelDistance = startPos - endPos;
    container.style.setProperty("--marquee-start", `${startPos}px`);
    container.style.setProperty("--marquee-end", `${endPos}px`);
  }

  const duration = Math.max(travelDistance / MARQUEE_SPEED_PX_PER_SEC, MIN_MARQUEE_DURATION_SEC);
  container.style.setProperty("--marquee-duration", `${duration}s`);
  content.style.animation = "none";
  void content.offsetWidth;
  content.style.animation = "";
  content.style.animationDuration = `${duration}s`;
}

export function useConstantMarqueeSpeed(
  containerRef: RefObject<HTMLElement | null>,
  contentRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  contentKey: string | number = "",
  mode: MarqueeMode = "pass"
) {
  const updateDuration = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!enabled || !container || !content) return;
    applyMarqueeStyles(container, content, mode);
  }, [enabled, contentKey, mode]);

  useLayoutEffect(() => {
    if (!enabled) return undefined;
    updateDuration();
    window.addEventListener("resize", updateDuration);
    const ResizeObserverCtor = (window as Window & { ResizeObserver?: new (cb: () => void) => ResizeObserverLike }).ResizeObserver;
    let resizeObserver: ResizeObserverLike | undefined;
    if (ResizeObserverCtor && containerRef.current) {
      resizeObserver = new ResizeObserverCtor(updateDuration);
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      window.removeEventListener("resize", updateDuration);
      resizeObserver?.disconnect();
    };
  }, [updateDuration, enabled]);
}
